import React, { ReactNode, useEffect } from 'react';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { AppContent, ErrorHandler, LoadingState } from '@common/components';
import { Col, Row } from 'antd';
import {
  OrganizationFacilityDetailsSection,
  OrganizationFacilityMenu,
} from '@institutions/components/organization-facility';
import { Colors } from 'works/legacy/lib/style-helper';
import { useQuery, useParams } from '@common/hooks';
import {
  organizationBusinessQuery,
  OrganizationBusinessQueryResponse,
  OrganizationBusinessQueryVariables,
} from '@institutions/graphql';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { OrganizationFacilityGroupsSection } from '@institutions/components/organization-facility/organization-facility-groups-section';

export function AdminOrganizationFacilityPage() {
  const { organizationFacilityId, page } = useParams<{
    organizationFacilityId: string;
    page: string;
  }>();
  const { data, error, loading } = useQuery<
    OrganizationBusinessQueryResponse,
    OrganizationBusinessQueryVariables
  >(organizationBusinessQuery, {
    variables: {
      id: organizationFacilityId,
    },
  });

  useEffect(
    () =>
      setBreadcrumbs([
        {
          name: 'Admin',
          path: '/works/admin',
        },
        {
          name: 'Locations',
          path: ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE,
        },
        {
          name: data?.organizationBusiness?.name || '',
          path: `${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/facility/${organizationFacilityId}`,
        },
      ]),
    [organizationFacilityId, data]
  );

  if (error) {
    return <ErrorHandler error={error} log="AdminOrganizationFacilityQuery" />;
  }

  const pagesContent: {
    [key: string]: ReactNode;
  } = {
    details: (
      <OrganizationFacilityDetailsSection
        facility={data?.organizationBusiness}
      />
    ),
    groups: (
      <OrganizationFacilityGroupsSection
        facility={data?.organizationBusiness}
      />
    ),
  };

  return (
    <AppContent>
      <div className="admin-organization-facility-page">
        <Row gutter={24}>
          <Col xxl={{ span: 6 }} xs={{ span: 8 }} sm={{ span: 8 }}>
            <OrganizationFacilityMenu
              facilityId={data?.organizationBusiness.id || ''}
              facilityName={data?.organizationBusiness.name || ''}
              page={page}
            />
          </Col>
          <Col xxl={{ span: 18 }} xs={{ span: 16 }} sm={{ span: 16 }}>
            {loading ? (
              <AppContent>
                <LoadingState />
              </AppContent>
            ) : (
              pagesContent[page]
            )}
          </Col>
        </Row>
        <style jsx>{`
          .admin-organization-facility-page :global(.ant-input-prefix) {
            color: ${Colors.steelDarker};
          }

          .admin-organization-facility-page :global(.ant-menu) {
            margin-top: 24px;
            border: none;
          }

          .admin-organization-facility-page :global(.ant-menu-item) {
            margin: 0;
            font-size: 16px;
            border-radius: 8px;
            display: flex;
            align-items: center;
          }

          .admin-organization-facility-page :global(.ant-menu-item > span) {
            width: 100%;
          }

          .admin-organization-facility-page :global(.ant-menu-item a),
          .organization-facility__menu__item-link {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
          }
        `}</style>
      </div>
    </AppContent>
  );
}
